import PageHeader from "@/components/PageHeader";
import { CtaBanner } from "@/components/CTA";
import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import {
  ExternalLink, ShieldCheck, Sparkles, Circle, ClipboardCheck,
  FileText, Landmark, GraduationCap, BookOpen,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/* Fact index — every quantitative or attributed claim on the site   */
/* ------------------------------------------------------------------ */
const factGroups = [
  {
    org: "GAO — U.S. Government Accountability Office",
    tag: "Federal · Independent",
    icon: <Landmark className="w-4 h-4" />,
    facts: [
      {
        stat: "Systemic gaps documented across DoD and VA transition programs",
        context: "WHO cites GAO oversight of federal transition assistance programs to frame the accountability gap in current post-service connections.",
        source: "GAO-24-106248 · Military-to-Civilian Transition Assistance",
        href: "https://www.gao.gov/products/gao-24-106248",
      },
    ],
  },
  {
    org: "DoD — Transition Assistance Program (TAP)",
    tag: "Federal · Primary source",
    icon: <ShieldCheck className="w-4 h-4" />,
    facts: [
      {
        stat: "~200,000 service members transition to civilian life each year",
        context: "Used across the site to establish the scale of the annual transition population. This figure is drawn from the DoD Transition Assistance Program.",
        source: "DoD TAP · dodtap.mil",
        href: "https://www.dodtap.mil/",
      },
    ],
  },
  {
    org: "VA — Department of Veterans Affairs",
    tag: "Federal · Primary source",
    icon: <ShieldCheck className="w-4 h-4" />,
    facts: [
      {
        stat: "~17.9M living U.S. veterans (2024)",
        context: "Used to frame the total veteran population WHO's coordination model serves. From the VA transition benefits landing page and the Veterans Benefits Administration.",
        source: "VA · benefits.va.gov/transition",
        href: "https://benefits.va.gov/transition/",
      },
    ],
  },
  {
    org: "HUD — AHAR · Veteran Homelessness",
    tag: "Federal · Point-in-Time",
    icon: <ShieldCheck className="w-4 h-4" />,
    facts: [
      {
        stat: "AHAR Part 1 · Point-in-Time estimates of homelessness in the U.S. (2024)",
        context: "WHO references the AHAR as the federal point-in-time source of veteran homelessness data. Precise figures are cited only where a directly linked source page is available.",
        source: "HUD · huduser.gov · AHAR 2024 Part 1",
        href: "https://www.huduser.gov/portal/datasets/ahar/2024-ahar-part-1-pit-estimates-of-homelessness-in-the-us.html",
      },
    ],
  },
  {
    org: "RAND Corporation",
    tag: "Independent · Nonpartisan",
    icon: <BookOpen className="w-4 h-4" />,
    facts: [
      {
        stat: "Policy recommendations to improve transition support for those leaving the military",
        context: "WHO's Problem page references RAND's conference proceedings on transition support. RAND has not evaluated, endorsed, funded, or partnered with WHO.",
        source: "RAND · CF-A1363-7",
        href: "https://www.rand.org/pubs/conf_proceedings/CFA1363-7.html",
      },
    ],
  },
  {
    org: "Penn State — Clearinghouse for Military Family Readiness",
    tag: "Independent · Academic",
    icon: <GraduationCap className="w-4 h-4" />,
    facts: [
      {
        stat: "The Right Connection Begins with Understanding the Whole Person",
        context: "WHO's Problem page cites Penn State research on evidence-based assessment and whole-person coordination. Penn State has not evaluated, endorsed, funded, or partnered with WHO.",
        source: "Penn State · Clearinghouse research references",
        href: "https://militaryfamilies.psu.edu/",
      },
    ],
  },
  {
    org: "IVMF — D'Aniello Institute for Veterans and Military Families",
    tag: "Independent · Benchmarking reference",
    icon: <BookOpen className="w-4 h-4" />,
    facts: [
      {
        stat: "Coordinated community networks, research, and career-transition frameworks",
        context: "WHO's Problem page includes an ecosystem-learning section referencing IVMF as an external source of research and benchmarking. IVMF, Syracuse University, AmericaServes, and related programs have not evaluated, funded, endorsed, or partnered with WHO.",
        source: "IVMF · ivmf.syracuse.edu",
        href: "https://ivmf.syracuse.edu/",
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/* Capability status — current / pilot / planned                     */
/* ------------------------------------------------------------------ */
const capabilities = [
  {
    label: "Live voice interaction with a WHO Guide (demo)",
    status: "Live",
    note: "Available at /experience. Powered by Claude and OpenAI TTS via the Emergent LLM key. Illustrative — not a signed pilot referral.",
  },
  {
    label: "Pittsburgh warm-handoff pilot",
    status: "Pilot open",
    note: "Now enrolling partners. Named partners will appear only after formal agreements are in place.",
  },
  {
    label: "Nine specialist AI agents (Healthcare · Benefits · Housing · Employment · Education · Transportation · Family · Community · Financial)",
    status: "In development",
    note: "Coordinated by a real WHO Guide. Each specialist agent's scope and verified partner network is developed in tandem with pilot partners.",
  },
  {
    label: "Verified partner directory and warm handoff engine",
    status: "In development",
    note: "Partners are added through the Pittsburgh pilot. National expansion depicted on the roadmap is planned, not current.",
  },
  {
    label: "Closed-loop outcome dashboards (Provider · Client Service · Reports)",
    status: "Illustrative",
    note: "Dashboards shown on the Impact page use fictional sample data and are labeled illustrative throughout.",
  },
  {
    label: "Grant- and contract-ready reporting exports",
    status: "Planned",
    note: "Report formats are being co-designed with pilot partners and will be released as the Pittsburgh pilot completes its first quarter.",
  },
  {
    label: "National multi-region and federal-integration expansion",
    status: "Roadmap",
    note: "Roadmap targets: Q3 2026 multi-region · Q1 2027 national partner network · Q3 2027+ data platform. All items are planned, not current.",
  },
];

/* ------------------------------------------------------------------ */
/* Terminology                                                       */
/* ------------------------------------------------------------------ */
const glossary = [
  {
    term: "Warm handoff",
    def: "A completed, accountable connection between a person and a receiving organization &mdash; not a referral, phone number, or resource list.",
  },
  {
    term: "WHO Guide™",
    def: "A real person, supported by AI, who coordinates a warm handoff from the first conversation through outcome confirmation.",
  },
  {
    term: "Closed-loop",
    def: "A pathway in which every referral has a tracked status &mdash; initiated, accepted, contacted, service begun, outcome confirmed, follow-up completed.",
  },
  {
    term: "Specialist AI agent",
    def: "A domain-specific AI system (e.g., Housing, Healthcare, Benefits) that supports a WHO Guide by handling triage, verified matching, and preparation.",
  },
  {
    term: "Verified partner",
    def: "An organization that has completed WHO's onboarding and confirmed its scope, eligibility criteria, geography, and capacity.",
  },
  {
    term: "Illustrative",
    def: "Data, dashboards, and screenshots labeled illustrative use fictional samples and do not represent actual WHO subscriber activity.",
  },
];

/* ------------------------------------------------------------------ */
/* Page                                                              */
/* ------------------------------------------------------------------ */
export default function Sources() {
  return (
    <div data-testid="sources-page">
      <Seo
        title="Facts & Sources | Warm Hand Off"
        description="Every statistic, source, and capability claim on the WHO — Warm Hand Off website, indexed and linked. Federal research citations, capability status (current, pilot, illustrative, planned), and terminology glossary."
        path="/sources"
      />
      <PageHeader
        eyebrow="Facts &amp; Sources"
        title="Every claim, indexed and linked."
        lede="WHO believes credibility should be checkable. This page indexes every statistic, source, and capability claim on the site. Federal research is linked to the source. Capability status is labeled as current, pilot, illustrative, or planned. Language and definitions are stated plainly."
      />

      {/* Intro copy */}
      <section className="py-14 lg:py-16 bg-white border-b border-borderSubtle" data-testid="sources-intro">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 space-y-5 text-lg text-slateSoft leading-relaxed">
            <p>
              WHO cites public federal data, independent research, and clearly labeled internal
              claims. Where a source has not evaluated, endorsed, funded, or partnered with WHO,
              this page states that plainly.
            </p>
            <p>
              Capabilities are labeled by their current status. WHO Guides™ and the Pittsburgh
              pilot are active. Multi-region expansion, national partner networks, and the outcome
              data platform are <span className="text-navy font-semibold">planned, not current</span>.
            </p>
          </div>
          <aside className="lg:col-span-5 p-6 rounded-lg bg-cream-panel border-l-4 border-gold" data-testid="sources-intro-callout">
            <p className="eyebrow text-gold-deep">Editorial standard</p>
            <p className="mt-3 text-navy leading-relaxed">
              &ldquo;Distinguish confirmed facts from planned capabilities and proposed
              partnerships. Do not invent outcomes, endorsements, or relationships.&rdquo;
            </p>
            <p className="mt-3 text-xs uppercase tracking-widest text-slateSoft">
              WHO Voice Guidelines &middot; 2026
            </p>
          </aside>
        </div>
      </section>

      {/* Fact index */}
      <section className="py-24 lg:py-28 bg-cream" data-testid="sources-fact-index">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-12">
            <p className="eyebrow text-gold-deep">Fact Index</p>
            <h2 className="mt-3 font-display font-black text-navy tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl">
              Where WHO&rsquo;s numbers come from.
            </h2>
          </div>

          <div className="space-y-6">
            {factGroups.map((g) => (
              <article
                key={g.org}
                className="p-7 rounded-xl bg-white border border-borderSubtle hover:border-gold/40 transition-colors"
                data-testid={`fact-group-${g.org.toLowerCase().replace(/\W+/g, "-").slice(0, 40)}`}
              >
                <div className="grid lg:grid-cols-12 gap-6">
                  <div className="lg:col-span-4">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-navy text-gold">
                        {g.icon}
                      </span>
                      <p className="text-[10px] uppercase tracking-widest text-gold-deep font-bold">
                        {g.tag}
                      </p>
                    </div>
                    <h3 className="mt-3 font-display font-bold text-navy text-lg leading-tight">
                      {g.org}
                    </h3>
                  </div>
                  <div className="lg:col-span-8 space-y-5">
                    {g.facts.map((f, i) => (
                      <div key={i} className="border-l-2 border-gold/50 pl-4">
                        <p className="font-display font-bold text-navy leading-snug">{f.stat}</p>
                        <p className="mt-1.5 text-slateSoft text-sm leading-relaxed">{f.context}</p>
                        <a
                          href={f.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-gold-deep hover:text-navy transition-colors"
                        >
                          {f.source}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>

          <p className="mt-10 text-xs text-slateSoft italic leading-relaxed max-w-3xl">
            References to external organizations, programs, and research are provided for
            informational and benchmarking purposes and do not imply endorsement, funding,
            affiliation, or partnership.
          </p>
        </div>
      </section>

      {/* Capability status */}
      <section className="py-24 lg:py-28 bg-navy-deep text-cream border-y border-white/5" data-testid="sources-capability-status">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-12">
            <p className="eyebrow text-gold">Capability Status</p>
            <h2 className="mt-3 font-display font-black text-cream tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl">
              What is <span className="text-gold">current</span>, what is <span className="text-gold">planned</span>.
            </h2>
            <p className="mt-5 text-lg text-cream/80 leading-relaxed">
              WHO&rsquo;s roadmap is deliberate. Some capabilities are live in the Pittsburgh
              pilot. Others are illustrative or planned. Every capability described on the site
              carries one of the labels below.
            </p>
          </div>

          {/* Legend */}
          <div className="mb-8 flex flex-wrap gap-2" data-testid="capability-legend">
            {[
              { s: "Live", desc: "In production or demonstrable now" },
              { s: "Pilot open", desc: "Active in the Pittsburgh pilot" },
              { s: "In development", desc: "Being built during the pilot" },
              { s: "Illustrative", desc: "Fictional samples for reference only" },
              { s: "Planned", desc: "On the roadmap; not current" },
              { s: "Roadmap", desc: "Future phase; timing subject to change" },
            ].map((l) => (
              <span
                key={l.s}
                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/15 text-cream/85"
              >
                <StatusBadge status={l.s} />
                <span className="text-cream/60 normal-case tracking-normal text-[11px]">{l.desc}</span>
              </span>
            ))}
          </div>

          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-3 bg-white/[0.03] border-b border-white/10">
              <div className="p-4 md:p-5 md:col-span-2">
                <p className="text-[10px] uppercase tracking-widest text-cream/50 font-bold">Capability</p>
              </div>
              <div className="p-4 md:p-5 border-t md:border-t-0 md:border-l border-white/10">
                <p className="text-[10px] uppercase tracking-widest text-cream/50 font-bold">Status</p>
              </div>
            </div>
            {capabilities.map((c, i) => (
              <div
                key={c.label}
                className="grid grid-cols-1 md:grid-cols-3 border-t border-white/10 hover:bg-white/[0.02] transition-colors"
                data-testid={`capability-row-${i}`}
              >
                <div className="p-4 md:p-5 md:col-span-2">
                  <p className="font-display font-bold text-cream text-sm leading-tight">{c.label}</p>
                  <p className="mt-1 text-cream/70 text-xs leading-relaxed">{c.note}</p>
                </div>
                <div className="p-4 md:p-5 border-t md:border-t-0 md:border-l border-white/10 flex items-start">
                  <StatusBadge status={c.status} large />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Glossary */}
      <section className="py-24 lg:py-28 bg-cream-panel border-b border-borderSubtle" data-testid="sources-glossary">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-12">
            <p className="eyebrow text-gold-deep">Terminology</p>
            <h2 className="mt-3 font-display font-black text-navy tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl">
              What we mean when we say it.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {glossary.map((g) => (
              <div
                key={g.term}
                className="p-6 bg-white rounded-lg border border-borderSubtle"
                data-testid={`glossary-${g.term.toLowerCase().replace(/\W+/g, "-")}`}
              >
                <p className="font-display font-black text-navy text-lg leading-tight">{g.term}</p>
                <p
                  className="mt-2 text-slateSoft leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: g.def }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reporting a correction */}
      <section className="py-16 lg:py-20 bg-white" data-testid="sources-correction">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 rounded-2xl bg-navy-deep text-cream p-8 lg:p-10 border border-gold/40 grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-8">
            <p className="eyebrow text-gold">Report a correction</p>
            <h3 className="mt-3 font-display font-black text-cream text-2xl lg:text-3xl leading-tight">
              See a claim that needs a source or an update?
            </h3>
            <p className="mt-3 text-cream/85 leading-relaxed">
              Every fact on this site should be verifiable. If you find a statistic without a link,
              a source that has moved, or a capability described in a way that overstates its
              current status, let us know and we will correct it.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/contact"
              data-testid="sources-correction-cta"
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-6 py-3.5 rounded-md transition-colors"
            >
              Send a correction
            </Link>
          </div>
        </div>
      </section>

      <CtaBanner
        eyebrow="Credibility is a practice."
        title="Explore the research behind WHO."
        primaryText="Read the Problem page"
        primaryTo="/problem"
        secondaryText="See the Impact page"
        secondaryTo="/impact"
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Status badge                                                      */
/* ------------------------------------------------------------------ */
function StatusBadge({ status, large }) {
  const map = {
    "Live":           { bg: "bg-gold text-navy",                              dot: "bg-navy"   },
    "Pilot open":     { bg: "bg-gold/25 text-cream border border-gold/60",     dot: "bg-gold animate-pulse" },
    "In development": { bg: "bg-white/[0.08] text-cream border border-white/25", dot: "bg-gold-light" },
    "Illustrative":   { bg: "bg-white/[0.05] text-cream/70 border border-white/15", dot: "bg-cream/50" },
    "Planned":        { bg: "bg-white/[0.05] text-cream/80 border border-white/15", dot: "bg-cream/60" },
    "Roadmap":        { bg: "bg-white/[0.05] text-cream/70 border border-white/15", dot: "bg-cream/50" },
  };
  const style = map[status] || map["Planned"];
  const sizeCls = large ? "text-[10px] px-3 py-1.5" : "text-[9px] px-2 py-1";
  return (
    <span className={`inline-flex items-center gap-1.5 uppercase tracking-widest font-bold rounded-full ${style.bg} ${sizeCls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {status}
    </span>
  );
}
